/**
 * Vehicle makes registry, slugifiers, and model mapping for ServiceBoss, ServiceByReg, and ServiceLad.
 *
 * Rules:
 * - No en or em dashes in copy or comments.
 * - Digital service history coverage is for supported makes from model year 2012.
 */

import { isServiceHistoryMakeAndYearEligible } from "../service-history-eligibility.js";

export const MAKES = Object.freeze([
  {
    slug: "audi",
    name: "Audi",
    file: "imgi_814_audi.svg",
    aliases: [],
    dbMakes: ["AUDI"],
    serviceHistory: true,
    group: "german",
    popularModels: ["A3", "A4", "A1", "A6", "Q3", "Q5", "TT"],
  },
  {
    slug: "bmw",
    name: "BMW",
    file: "imgi_818_bmw.svg",
    aliases: [],
    dbMakes: ["BMW"],
    serviceHistory: true,
    group: "german",
    popularModels: ["1 Series", "3 Series", "5 Series", "X1", "X3", "X5"],
  },
  {
    slug: "mercedes",
    name: "Mercedes-Benz",
    file: "imgi_840_mercedes.svg",
    aliases: ["mercedes-benz", "merc"],
    dbMakes: ["MERCEDES-BENZ", "MERCEDES", "MERCEDES BENZ"],
    serviceHistory: true,
    group: "german",
    popularModels: ["A-Class", "C-Class", "E-Class", "CLA", "GLA", "GLC"],
  },
  {
    slug: "volkswagen",
    name: "Volkswagen",
    file: "imgi_860_volkswagen.svg",
    aliases: ["vw"],
    dbMakes: ["VOLKSWAGEN", "VW"],
    serviceHistory: true,
    group: "german",
    popularModels: ["Golf", "Polo", "Passat", "Tiguan", "T-Roc", "Arteon", "Up"],
  },
  {
    slug: "ford",
    name: "Ford",
    file: "imgi_826_ford.svg",
    aliases: [],
    dbMakes: ["FORD"],
    serviceHistory: true,
    group: "popular",
    popularModels: ["Fiesta", "Focus", "Puma", "Kuga", "Mondeo", "EcoSport"],
  },
  {
    slug: "land-rover",
    name: "Land Rover",
    file: "imgi_835_landrover.svg",
    aliases: ["landrover", "range-rover"],
    dbMakes: ["LAND ROVER", "LANDROVER", "RANGE ROVER"],
    serviceHistory: true,
    group: "british",
    popularModels: ["Range Rover Evoque", "Range Rover Sport", "Discovery Sport", "Discovery", "Defender"],
  },
  {
    slug: "vauxhall",
    name: "Vauxhall",
    file: "imgi_859_vauxhall.svg",
    aliases: [],
    dbMakes: ["VAUXHALL"],
    serviceHistory: true,
    group: "british",
    popularModels: ["Corsa", "Astra", "Mokka", "Insignia", "Crossland"],
  },
  {
    slug: "toyota",
    name: "Toyota",
    file: "imgi_858_toyota.svg",
    aliases: [],
    dbMakes: ["TOYOTA"],
    serviceHistory: true,
    group: "asian",
    popularModels: ["Yaris", "Corolla", "Auris", "Aygo", "RAV4", "C-HR"],
  },
  {
    slug: "nissan",
    name: "Nissan",
    file: "imgi_844_nissan.svg",
    aliases: [],
    dbMakes: ["NISSAN"],
    serviceHistory: true,
    group: "asian",
    popularModels: ["Qashqai", "Juke", "Micra", "X-Trail", "Leaf"],
  },
  {
    slug: "porsche",
    name: "Porsche",
    file: "imgi_878_porsche.svg",
    aliases: [],
    dbMakes: ["PORSCHE"],
    serviceHistory: true,
    group: "german",
    popularModels: ["911", "Cayenne", "Macan", "Panamera", "Boxster", "Cayman"],
  },
  {
    slug: "jaguar",
    name: "Jaguar",
    file: "imgi_832_jaguar.svg",
    aliases: [],
    dbMakes: ["JAGUAR"],
    serviceHistory: true,
    group: "british",
    popularModels: ["XE", "XF", "F-Pace", "E-Pace", "F-Type"],
  },
  {
    slug: "volvo",
    name: "Volvo",
    file: "imgi_861_volvo.svg",
    aliases: [],
    dbMakes: ["VOLVO"],
    serviceHistory: false,
    group: "popular",
    popularModels: ["XC40", "XC60", "XC90", "V40", "V60"],
  },
  {
    slug: "mini",
    name: "MINI",
    file: "imgi_842_mini.svg",
    aliases: [],
    dbMakes: ["MINI"],
    serviceHistory: true,
    group: "british",
    popularModels: ["Hatch", "Cooper", "Countryman", "Clubman"],
  },
  {
    slug: "hyundai",
    name: "Hyundai",
    file: "imgi_830_hyundai.svg",
    aliases: [],
    dbMakes: ["HYUNDAI"],
    serviceHistory: false,
    group: "asian",
    popularModels: ["i10", "i20", "i30", "Tucson", "Kona"],
  },
  {
    slug: "kia",
    name: "Kia",
    file: "imgi_834_kia.svg",
    aliases: [],
    dbMakes: ["KIA"],
    serviceHistory: false,
    group: "asian",
    popularModels: ["Sportage", "Ceed", "Picanto", "Rio", "Niro"],
  },
  {
    slug: "peugeot",
    name: "Peugeot",
    file: "imgi_847_peugeot.svg",
    aliases: [],
    dbMakes: ["PEUGEOT"],
    serviceHistory: true,
    group: "popular",
    popularModels: ["208", "2008", "308", "3008", "5008"],
  },
  {
    slug: "renault",
    name: "Renault",
    file: "imgi_850_renault.svg",
    aliases: [],
    dbMakes: ["RENAULT"],
    serviceHistory: false,
    group: "popular",
    popularModels: ["Clio", "Captur", "Megane", "Kadjar", "Zoe"],
  },
  {
    slug: "skoda",
    name: "Škoda",
    file: "imgi_853_skoda.svg",
    aliases: ["skoda"],
    dbMakes: ["SKODA", "ŠKODA"],
    serviceHistory: true,
    group: "popular",
    popularModels: ["Octavia", "Fabia", "Superb", "Karoq", "Kodiaq"],
  },
  {
    slug: "seat",
    name: "SEAT",
    file: "imgi_852_seat.svg",
    aliases: [],
    dbMakes: ["SEAT"],
    serviceHistory: true,
    group: "popular",
    popularModels: ["Ibiza", "Leon", "Ateca", "Arona"],
  },
  {
    slug: "cupra",
    name: "Cupra",
    file: "imgi_852_seat.svg",
    aliases: [],
    dbMakes: ["CUPRA"],
    serviceHistory: true,
    group: "popular",
    popularModels: ["Formentor", "Leon", "Ateca", "Born"],
  },
  {
    slug: "honda",
    name: "Honda",
    file: "imgi_827_honda.svg",
    aliases: [],
    dbMakes: ["HONDA"],
    serviceHistory: false,
    group: "asian",
    popularModels: ["Civic", "Jazz", "CR-V", "HR-V"],
  },
  {
    slug: "tesla",
    name: "Tesla",
    file: "imgi_857_tesla.svg",
    aliases: [],
    dbMakes: ["TESLA"],
    serviceHistory: false,
    group: "popular",
    popularModels: ["Model 3", "Model Y", "Model S", "Model X"],
  },
  {
    slug: "aston-martin",
    name: "Aston Martin",
    file: "imgi_813_astonmartin.svg",
    aliases: ["astonmartin"],
    dbMakes: ["ASTON MARTIN", "ASTONMARTIN"],
    serviceHistory: false,
    group: "british",
    popularModels: ["Vantage", "DB11", "DB9", "DBS"],
  },
  {
    slug: "bentley",
    name: "Bentley",
    file: "imgi_851_bentley.svg",
    aliases: [],
    dbMakes: ["BENTLEY"],
    serviceHistory: false,
    group: "british",
    popularModels: ["Continental GT", "Flying Spur", "Bentayga"],
  },
  {
    slug: "alfa-romeo",
    name: "Alfa Romeo",
    file: "imgi_817_alfaromeo.svg",
    aliases: ["alfaromeo"],
    dbMakes: ["ALFA ROMEO", "ALFAROMEO"],
    serviceHistory: false,
    group: "popular",
    popularModels: ["Giulia", "Stelvio", "Giulietta", "Mito"],
  },
  {
    slug: "lexus",
    name: "Lexus",
    file: "imgi_836_lexus.svg",
    aliases: [],
    dbMakes: ["LEXUS"],
    serviceHistory: true,
    group: "asian",
    popularModels: ["IS", "NX", "RX", "CT", "UX"],
  },
  {
    slug: "mazda",
    name: "Mazda",
    file: "imgi_839_mazda.svg",
    aliases: [],
    dbMakes: ["MAZDA"],
    serviceHistory: true,
    group: "asian",
    popularModels: ["Mazda3", "Mazda6", "CX-5", "CX-30", "MX-5"],
  },
  {
    slug: "fiat",
    name: "Fiat",
    file: "imgi_825_fiat.svg",
    aliases: [],
    dbMakes: ["FIAT"],
    serviceHistory: false,
    group: "popular",
    popularModels: ["500", "Panda", "Tipo", "500X"],
  },
  {
    slug: "ds",
    name: "DS",
    file: "imgi_824_ds.svg",
    aliases: ["ds-automobiles"],
    dbMakes: ["DS", "DS AUTOMOBILES"],
    serviceHistory: false,
    group: "popular",
    popularModels: ["DS 3", "DS 7 Crossback", "DS 4"],
  },
  {
    slug: "mg",
    name: "MG",
    file: "imgi_841_mg.svg",
    aliases: [],
    dbMakes: ["MG"],
    serviceHistory: false,
    group: "british",
    popularModels: ["MG3", "MG ZS", "MG HS", "MG4"],
  },
  {
    slug: "mitsubishi",
    name: "Mitsubishi",
    file: "imgi_843_mitsubishi.svg",
    aliases: [],
    dbMakes: ["MITSUBISHI"],
    serviceHistory: false,
    group: "asian",
    popularModels: ["Outlander", "ASX", "L200", "Mirage"],
  },
  {
    slug: "subaru",
    name: "Subaru",
    file: "imgi_856_subaru.svg",
    aliases: [],
    dbMakes: ["SUBARU"],
    serviceHistory: false,
    group: "asian",
    popularModels: ["Impreza", "Forester", "Outback", "XV"],
  },
  {
    slug: "suzuki",
    name: "Suzuki",
    file: "imgi_855_suzuki.svg",
    aliases: [],
    dbMakes: ["SUZUKI"],
    serviceHistory: false,
    group: "asian",
    popularModels: ["Swift", "Vitara", "Ignis", "S-Cross"],
  },
  {
    slug: "smart",
    name: "smart",
    file: "imgi_854_smart.svg",
    aliases: [],
    dbMakes: ["SMART"],
    serviceHistory: false,
    group: "german",
    popularModels: ["Fortwo", "Forfour"],
  },
  {
    slug: "isuzu",
    name: "Isuzu",
    file: "imgi_865_isuzu.svg",
    aliases: [],
    dbMakes: ["ISUZU"],
    serviceHistory: false,
    group: "asian",
    popularModels: ["D-Max"],
  },
  {
    slug: "opel",
    name: "Opel",
    file: "imgi_846_opel.svg",
    aliases: [],
    dbMakes: ["OPEL"],
    serviceHistory: true,
    group: "popular",
    popularModels: ["Corsa", "Astra", "Insignia"],
  },
  {
    slug: "pontiac",
    name: "Pontiac",
    file: "imgi_848_pontiac.svg",
    aliases: [],
    dbMakes: ["PONTIAC"],
    serviceHistory: false,
    group: "popular",
    popularModels: ["Firebird", "GTO"],
  },
]);

/**
 * Normalise a make slug or alias (e.g. "vw" -> "volkswagen").
 */
export function slugifyMake(input = "") {
  const clean = String(input || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  if (!clean) return "";
  const found = findMakeBySlug(clean);
  return found ? found.slug : clean;
}

/**
 * Find make definition by slug, alias, or display name.
 */
export function findMakeBySlug(slugOrAlias = "") {
  const needle = String(slugOrAlias || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  if (!needle) return null;
  return MAKES.find((m) => m.slug === needle || m.aliases.includes(needle)) || null;
}

/**
 * Find make definition from raw DVLA/DVSA make string (e.g. "VOLKSWAGEN" -> VW definition).
 */
export function findMakeByDbMake(dbMake = "") {
  const needle = String(dbMake || "").toUpperCase().trim();
  if (!needle) return null;
  return MAKES.find((m) => m.dbMakes.some((item) => item.toUpperCase() === needle)) || null;
}

/**
 * Slugify a vehicle model name (e.g. "Golf GTD" -> "golf-gtd", "3 Series" -> "3-series").
 */
export function slugifyModel(model = "") {
  return String(model || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Formats a model slug back into a human-readable title (e.g. "golf-gtd" -> "Golf GTD").
 */
export function formatModelTitle(modelSlug = "", originalModelName = "") {
  if (originalModelName && String(originalModelName).trim()) {
    return String(originalModelName).trim();
  }
  const clean = String(modelSlug || "").trim().toLowerCase();
  // Special handling for single-letter class models like "c-class" -> "C-Class"
  const classMatch = clean.match(/^([a-z])-class$/);
  if (classMatch) {
    return `${classMatch[1].toUpperCase()}-Class`;
  }
  return clean
    .split("-")
    .filter(Boolean)
    .map((word) => {
      const lower = word.toLowerCase();
      // Keep common acronyms uppercase
      if (["gti", "gtd", "gte", "amg", "rs", "st", "m", "tt", "suv", "mpv", "4x4", "ev"].includes(lower)) {
        return lower.toUpperCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
}

/**
 * URL for a make overview page.
 */
export function makePageUrl(siteKey, makeSlug) {
  const norm = slugifyMake(makeSlug);
  return `/makes/${norm}`;
}

/**
 * URL for a model overview page.
 */
export function modelPageUrl(siteKey, makeSlug, modelSlug) {
  const normMake = slugifyMake(makeSlug);
  const normModel = slugifyModel(modelSlug);
  return `/makes/${normMake}/${normModel}`;
}
